import { useEffect, useRef, lazy, Suspense } from "react";
import "@/App.css";
import Lenis from "lenis";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import Navbar from "@/components/Navbar";
import Hero from "@/components/Hero";
import Footer from "@/components/Footer";
import { PageSeo } from "@/content";
import { CITIES, PRODUCT_PREFIXES } from "@/content/routes";
import { AUDIENCE_PAGES } from "@/content/audience";

// Code-split: everything below the hero fold and every inner page loads on
// demand — keeps the initial bundle (and mobile TBT/TTI) small.
const Marquee = lazy(() => import("@/components/Marquee"));
const DataEngine = lazy(() => import("@/components/DataEngine"));
const ProductSuite = lazy(() => import("@/components/ProductSuite"));
const StatsBand = lazy(() => import("@/components/StatsBand"));
const Manifesto = lazy(() => import("@/components/Manifesto"));
const Industries = lazy(() => import("@/components/Industries"));
const Testimonials = lazy(() => import("@/components/Testimonials"));
const Compliance = lazy(() => import("@/components/Compliance"));
const Usp = lazy(() => import("@/components/Usp"));
const Faq = lazy(() => import("@/components/Faq"));
const DemoForm = lazy(() => import("@/components/DemoForm"));
const ContentSections = lazy(() => import("@/components/ContentSections"));
const ProductLinks = lazy(() => import("@/components/ProductLinks"));
const AudienceUseCase = lazy(() => import("@/components/AudienceUseCase"));

const AdminLeads = lazy(() => import("@/components/AdminLeads"));
const About = lazy(() => import("@/pages/About"));
const Solutions = lazy(() => import("@/pages/Solutions"));
const Blogs = lazy(() => import("@/pages/Blogs"));
const BlogPost = lazy(() => import("@/pages/BlogPost"));
const Contact = lazy(() => import("@/pages/Contact"));
const Legal = lazy(() => import("@/pages/Legal"));
const SolutionDetail = lazy(() => import("@/pages/SolutionDetail"));
const LocationPage = lazy(() => import("@/pages/LocationPage"));
const ProductLocationPage = lazy(() => import("@/pages/ProductLocationPage"));
const NotFound = lazy(() => import("@/pages/NotFound"));

const ScrollToTop = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const firstRoute = useRef(true);
  useEffect(() => {
    // normalize trailing-slash URLs (e.g. from the location mapping sheet)
    if (pathname.length > 1 && pathname.endsWith("/")) {
      navigate(pathname.slice(0, -1), { replace: true });
      return;
    }
    window.scrollTo(0, 0);
    // SPA virtual pageviews: Openink AI-SEO + GA4 (skip initial load — both
    // snippets already send the first pageview from index.html)
    if (firstRoute.current) {
      firstRoute.current = false;
      return;
    }
    if (typeof window.OpeninkTrack === "function") {
      window.OpeninkTrack("virtual_pageview", { path: window.location.pathname });
    }
    if (typeof window.gtag === "function") {
      window.gtag("event", "page_view", {
        page_path: window.location.pathname,
        page_title: document.title,
      });
    }
  }, [pathname]);
  return null;
};

const Landing = () => {
  useEffect(() => {
    if (window.location.hash) {
      const el = document.querySelector(window.location.hash);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 400);
    }
  }, []);
  return (
    <>
      <PageSeo route="/" />
      <Navbar />
      <main>
        <Hero />
        <Suspense fallback={null}>
          <Marquee />
          <DataEngine />
          <ProductSuite />
          <StatsBand />
          <Manifesto />
          <Industries />
          <ContentSections route="/" exclude={["one unified platform", "the manifesto", "industries we work for"]} testId="home-content-sections" />
          <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 pb-20">
            <ProductLinks />
          </div>
          <AudienceUseCase data={AUDIENCE_PAGES["/"]} testId="home-audience-usecase" />
          <Testimonials />
          <Compliance />
          <Usp />
          <Faq />
          <DemoForm />
        </Suspense>
      </main>
      <Footer />
    </>
  );
};

function App() {
  useEffect(() => {
    // Smooth-scroll engine only on precise pointers — skips the per-frame rAF
    // loop on touch devices to keep mobile TBT low
    const fine = window.matchMedia("(pointer: fine)").matches;
    const lenis = fine ? new Lenis({ lerp: 0.08, smoothWheel: true }) : null;
    let rafId;
    if (lenis) {
      const raf = (time) => {
        lenis.raf(time);
        rafId = requestAnimationFrame(raf);
      };
      rafId = requestAnimationFrame(raf);
    }
    const onAnchor = (e) => {
      const a = e.target.closest('a[href^="#"]');
      if (!a) return;
      const target = document.querySelector(a.getAttribute("href"));
      if (target) {
        e.preventDefault();
        if (lenis) lenis.scrollTo(target, { offset: -80 });
        else target.scrollIntoView({ behavior: "smooth" });
      }
    };
    document.addEventListener("click", onAnchor);
    return () => {
      if (lenis) {
        cancelAnimationFrame(rafId);
        lenis.destroy();
      }
      document.removeEventListener("click", onAnchor);
    };
  }, []);

  return (
    <div className="app-bg text-slate-100 min-h-screen">
      <div className="grain-overlay" />
      <BrowserRouter>
        <ScrollToTop />
        <Suspense fallback={<div className="min-h-screen" />}>
          <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/about-us" element={<About />} />
          <Route path="/solutions" element={<Solutions />} />
          <Route path="/blog" element={<Blogs />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/contact-us" element={<Contact />} />
          <Route path="/privacy" element={<Legal pageKey="privacy" />} />
          <Route path="/terms" element={<Legal pageKey="terms" />} />
          <Route path="/shipping" element={<Legal pageKey="shipping" />} />
          <Route path="/refund" element={<Legal pageKey="refund" />} />
          <Route path="/disclaimer" element={<Legal pageKey="disclaimer" />} />
          <Route path="/cookies" element={<Legal pageKey="cookies" />} />
          <Route path="/electronic-data-capture-software-for-clinical-trials" element={<SolutionDetail slug="electronic-data-capture-software-for-clinical-trials" />} />
          <Route path="/electronic-consent-management-system" element={<SolutionDetail slug="electronic-consent-management-system" />} />
          <Route path="/electronic-patient-reported-outcomes-software" element={<SolutionDetail slug="electronic-patient-reported-outcomes-software" />} />
          <Route path="/interactive-web-response-system" element={<SolutionDetail slug="interactive-web-response-system" />} />
          <Route path="/video-consultation-module-for-clinical-trials" element={<SolutionDetail slug="video-consultation-module-for-clinical-trials" />} />
          <Route path="/clinical-trial-participant-tracker-software" element={<SolutionDetail slug="clinical-trial-participant-tracker-software" />} />
          <Route path="/best-electronic-data-capture-software-for-clinical-trials-in-mumbai" element={<LocationPage city="Mumbai" citySlug="mumbai" />} />
          <Route path="/best-electronic-data-capture-software-for-clinical-trials-in-hyderabad" element={<LocationPage city="Hyderabad" citySlug="hyderabad" />} />
          <Route path="/best-electronic-data-capture-software-for-clinical-trials-in-ahemdabad" element={<LocationPage city="Ahmedabad" citySlug="ahemdabad" />} />
          <Route path="/best-electronic-data-capture-software-for-clinical-trials-in-pune" element={<LocationPage city="Pune" citySlug="pune" />} />
          <Route path="/best-electronic-data-capture-software-for-clinical-trials-in-delhi" element={<LocationPage city="Delhi" citySlug="delhi" />} />
          {Object.keys(CITIES).flatMap((citySlug) =>
            Object.entries(PRODUCT_PREFIXES).map(([product, prefix]) => (
              <Route
                key={`${product}-${citySlug}`}
                path={`/${prefix}${citySlug}`}
                element={<ProductLocationPage product={product} citySlug={citySlug} />}
              />
            ))
          )}
          <Route path="/admin" element={<AdminLeads />} />
          <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
      <Toaster position="bottom-right" theme="dark" />
    </div>
  );
}

export default App;
